import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Input } from '../../components/ui/input';
import { cn, formatCurrency } from '../../lib/utils';
import {
  ArrowLeft, ArrowRight, Check, AlertTriangle, CheckCircle2,
  Sparkles, FileText, Shield, Clock, DollarSign, Send,
  Loader2, Lock, Plus, X,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAppMode } from '../../contexts/AppModeContext';

const SAMPLE_SUPERBILL = "https://customer-assets.emergentagent.com/job_revenue-cycle-1/artifacts/drd5xsw8_superbill%20sample.png";

const DEMO_EXTRACTED = {
  patient: { name: { v: 'Mary L. Allen', c: 98 }, dob: { v: '1980-02-03', c: 99 }, memberId: { v: 'AUD-456789123', c: 96 }, insurance: { v: 'Blue Cross Blue Shield', c: 97 } },
  provider: { name: { v: 'ABC Audiology', c: 99 }, npi: { v: '1234567890', c: 98 }, dos: { v: '2011-03-20', c: 94 }, location: { v: 'Northport, WA', c: 92 } },
  diagnoses: [
    { code: '389.00', desc: 'Hearing Loss, Conductive', c: 98 },
    { code: '389.11', desc: 'Hearing Loss, Sensory', c: 95 },
    { code: '389.10', desc: 'Hearing Loss, Sensorineural', c: 92 },
  ],
  procedures: [
    { cpt: '92551', desc: 'Screening, pure tone, air only', fee: 45.00, c: 99 },
    { cpt: '92552', desc: 'PT audiometry; air', fee: 85.00, c: 97 },
    { cpt: '92553', desc: 'PT audiometry; air & bone', fee: 125.00, c: 94 },
    { cpt: 'V5010', desc: 'Assessment for HA(s)', fee: 150.00, c: 96 },
  ],
  total: 405.00,
};

const DEMO_VALIDATION = {
  score: 94,
  status: 'Likely to pass',
  estReimburse: 199.00,
  estDays: '14\u201321',
  checks: [
    { name: 'LCD/NCD Policy Check', pass: true, msg: 'All diagnoses meet medical necessity requirements' },
    { name: 'CCI Edit Check', pass: true, msg: 'No bundling conflicts detected' },
    { name: 'Modifier Validation', pass: true, msg: 'Modifiers correctly applied' },
    { name: 'Payer Rules', pass: false, msg: 'This patient has had 2 rapid strep tests this year. Blue Cross requires prior authorization if they have more than 3. You\'re within the limit today, but flag this patient for next time.' },
    { name: 'Provider Credentialing', pass: true, msg: 'Provider is in-network and credentialed' },
    { name: 'Timely Filing', pass: true, msg: 'Within 90-day filing window' },
  ],
  recs: [
    'Consider adding modifier 25 to 99214 if E/M was significant and separately identifiable from strep test.',
    'Patient approaching prior auth threshold for rapid strep tests. Next visit may require authorization.',
  ],
};

// ── Confidence field ──
const ConfField = ({ label, value, confidence, onChange }) => {
  const isHigh = confidence >= 95;
  const isMed = confidence >= 70 && confidence < 95;
  const isLow = confidence < 70;
  const dotColor = isHigh ? '#22c55e' : isMed ? '#f59e0b' : '#ef4444';
  const border = isLow ? '1px solid #fca5a5' : isMed ? '0.5px solid #fde68a' : '0.5px solid #e0e0e0';
  const bg = isLow ? '#fef2f2' : isMed ? '#fffbeb' : '#fff';

  return (
    <div style={{ marginBottom: 10 }}>
      <p style={{ fontSize: 10, color: '#aaaaaa', textTransform: 'uppercase', marginBottom: 3 }}>{label}</p>
      <div className="relative">
        <Input
          value={value}
          onChange={e => onChange?.(e.target.value)}
          className="pr-14"
          style={{ height: 36, borderRadius: 7, border, background: bg, fontSize: 13, color: isLow ? '#b91c1c' : '#111' }}
          data-testid={`field-${label.toLowerCase().replace(/\s/g, '-')}`}
        />
        <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1">
          <div style={{ width: 6, height: 6, borderRadius: '50%', background: dotColor }} />
          <span style={{ fontSize: 10, color: dotColor }}>{confidence}%</span>
        </div>
      </div>
      {isLow && <p style={{ fontSize: 10, color: '#b91c1c', marginTop: 2 }}>Low confidence — please verify</p>}
    </div>
  );
};

const RevQAIClaimsWizard = () => {
  const { isDemo } = useAppMode();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [validation, setValidation] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [acknowledged, setAcknowledged] = useState(new Set());
  const [extractError, setExtractError] = useState(null);

  useEffect(() => {
    if (isDemo) {
      const t = setTimeout(() => { setData(DEMO_EXTRACTED); setLoading(false); }, 1800);
      return () => clearTimeout(t);
    }
    // Real mode: AI extraction is not yet wired to a backend service.
    // Surface a neutral "feature unavailable" message rather than
    // fabricating extracted PHI from a demo fixture.
    setLoading(false);
    setExtractError('AI claims extraction is not yet available in real mode. Use the manual claim wizard from Claims → New for now.');
  }, [isDemo]);

  useEffect(() => {
    if (!isDemo) return;
    if (step === 2 && !validation) {
      const t = setTimeout(() => setValidation(DEMO_VALIDATION), 1200);
      return () => clearTimeout(t);
    }
  }, [step, validation, isDemo]);

  const lowCount = data ? Object.values({ ...data.patient, ...data.provider }).filter(f => f.c < 70).length : 0;
  const reviewCount = data ? Object.values({ ...data.patient, ...data.provider }).filter(f => f.c < 95).length : 0;
  const warningCount = validation ? validation.checks.filter(c => !c.pass && !acknowledged.has(c.name)).length : 0;
  const scoreColor = (validation?.score || 0) >= 90 ? '#22c55e' : (validation?.score || 0) >= 70 ? '#f59e0b' : '#ef4444';

  const handleSubmit = () => {
    setSubmitting(true);
    setTimeout(() => { setSubmitting(false); setSubmitted(true); toast.success('Claim submitted!'); }, 1500);
  };

  const handleExit = () => {
    if (window.confirm('Exit? Your progress will be saved as a draft.')) navigate('/revq/ai-claims');
  };

  // ── Success State ──
  if (submitted) {
    return (
      <div className="flex items-center justify-center py-12" data-testid="wizard-success">
        <div style={{ maxWidth: 560, textAlign: 'center' }}>
          {/* Animated checkmark */}
          <div className="mx-auto" style={{ width: 48, height: 48, borderRadius: '50%', background: '#22c55e', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Check className="h-6 w-6 text-white" style={{ animation: 'fadeIn 0.6s ease-out' }} />
          </div>
          <h2 style={{ fontSize: 22, fontWeight: 500, color: '#111', marginTop: 20 }}>Claim submitted.</h2>
          <p style={{ fontSize: 14, color: '#555', marginTop: 8 }}>Mary L. Allen &middot; ABC Audiology &middot; $199 estimated reimbursement</p>

          {/* Tracking info */}
          <div style={{ background: '#f9fafb', borderRadius: 10, padding: '14px 18px', marginTop: 20, textAlign: 'left' }}>
            <p style={{ fontSize: 13, fontFamily: 'monospace', color: '#333' }}>Claim ID: CLM-2026-00847</p>
            <p style={{ fontSize: 12, color: '#888', marginTop: 4 }}>Submitted: Today at {new Date().toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })} via EDI 837</p>
            <p style={{ fontSize: 12, color: '#888' }}>Expected: 14–21 business days</p>
            <p style={{ fontSize: 12, color: '#888' }}>Payer: Blue Cross Blue Shield</p>
          </div>

          {/* First-pass prediction */}
          <div style={{ background: '#f0fdf4', border: '0.5px solid #bbf7d0', borderRadius: 10, padding: '12px 16px', marginTop: 12, textAlign: 'left' }}>
            <p style={{ fontSize: 13, color: '#166534' }}>94% first-pass prediction — this claim is likely to be paid without rework.</p>
          </div>

          {/* Running total */}
          <p style={{ fontSize: 12, color: '#888', marginTop: 12 }}>Your practice's first-pass rate this month: 97.2% &middot; 4 claims &middot; $1,840 collected</p>

          {/* Actions */}
          <div className="flex items-center justify-center gap-3" style={{ marginTop: 24 }}>
            <button
              onClick={() => {
                setSubmitted(false); setStep(1); setData(null); setValidation(null);
                if (isDemo) {
                  setLoading(true);
                  setTimeout(() => { setData(DEMO_EXTRACTED); setLoading(false); }, 1800);
                } else {
                  setExtractError('AI claims extraction is not yet available in real mode. Use the manual claim wizard from Claims → New for now.');
                }
              }}
              style={{ height: 40, padding: '0 20px', borderRadius: 8, background: '#6b5ce7', color: '#fff', fontWeight: 500, fontSize: 13, border: 'none', cursor: 'pointer' }}
              data-testid="submit-another-btn"
            >Submit another claim</button>
            <button
              onClick={() => navigate('/revq/claims')}
              style={{ height: 40, padding: '0 20px', borderRadius: 8, background: '#fff', color: '#555', fontWeight: 500, fontSize: 13, border: '0.5px solid #d1d5db', cursor: 'pointer' }}
              data-testid="view-claims-btn"
            >View in claims queue</button>
          </div>
        </div>
      </div>
    );
  }

  const STEPS = [
    { num: 1, label: 'Review data' },
    { num: 2, label: 'Validate' },
    { num: 3, label: 'Submit' },
  ];

  return (
    <div className="space-y-5" data-testid="ai-claims-wizard">
      {/* Wizard Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <h1 style={{ fontSize: 18, fontWeight: 500, color: '#111' }}>AI Claim Submit</h1>
            <span style={{ fontSize: 11, background: '#f0edff', color: '#5b21b6', borderRadius: 20, padding: '2px 8px', fontWeight: 500 }}>Echo-Powered</span>
          </div>
          <p style={{ fontSize: 12, color: '#aaaaaa', marginTop: 2 }}>
            {data ? `${data.patient.name.v} · ${data.provider.name.v} · Superbill uploaded` : 'Processing document...'}
          </p>
        </div>
        <button onClick={handleExit} style={{ fontSize: 11, color: '#aaa', background: 'none', border: 'none', cursor: 'pointer' }}
          className="hover:text-red-500 transition-colors" data-testid="exit-wizard">Exit wizard</button>
      </div>

      {/* Stepper */}
      <div className="flex items-center justify-center gap-0" style={{ maxWidth: 480, margin: '0 auto' }}>
        {STEPS.map((s, i) => (
          <div key={s.num} className="flex items-center">
            <div className="flex flex-col items-center">
              <div
                onClick={() => step > s.num && setStep(s.num)}
                className={cn("flex items-center justify-center transition-all", step > s.num && "cursor-pointer")}
                style={{
                  width: 28, height: 28, borderRadius: '50%',
                  background: step >= s.num ? '#111' : '#f0f0f0',
                  color: step >= s.num ? '#fff' : '#aaa',
                  fontSize: 12, fontWeight: 500,
                  border: step === s.num ? '3px solid rgba(107,92,231,0.3)' : step >= s.num ? 'none' : '0.5px solid #e0e0e0',
                }}
                data-testid={`step-${s.num}`}
              >
                {step > s.num ? <Check className="h-3.5 w-3.5" /> : s.num}
              </div>
              <span style={{ fontSize: 12, fontWeight: step >= s.num ? 500 : 400, color: step >= s.num ? '#111' : '#aaa', marginTop: 6 }}>{s.label}</span>
            </div>
            {i < 2 && (
              <div style={{
                width: 60, height: 1, margin: '0 12px',
                marginBottom: 24,
                background: step > s.num ? '#111' : '#e0e0e0',
                borderTop: step > s.num ? 'none' : '1px dashed #e0e0e0',
              }} />
            )}
          </div>
        ))}
      </div>

      {/* ═══ STEP 1 — Review Data ═══ */}
      {step === 1 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {/* Left: Document */}
          <div style={{ background: '#fff', border: '0.5px solid #e8e8e8', borderRadius: 12, overflow: 'hidden' }}>
            <div className="flex items-center justify-between" style={{ padding: '10px 16px', borderBottom: '0.5px solid #f0f0f0' }}>
              <div className="flex items-center gap-2">
                <FileText className="h-4 w-4" style={{ color: '#555' }} />
                <span style={{ fontSize: 13, fontWeight: 500, color: '#111' }}>Original document</span>
                <span style={{ fontSize: 10, color: '#aaa', background: '#f5f5f5', borderRadius: 4, padding: '1px 6px' }}>Superbill — ABC Audiology</span>
              </div>
              <span style={{ fontSize: 11, color: '#aaa' }}>Page 1 of 2</span>
            </div>
            <div className="bg-slate-100 overflow-auto" style={{ height: 500 }}>
              {loading ? (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center">
                    <Loader2 className="h-8 w-8 animate-spin text-slate-400 mx-auto mb-3" />
                    <p style={{ fontSize: 13, color: '#888' }}>Processing with AI...</p>
                  </div>
                </div>
              ) : (
                <img src={SAMPLE_SUPERBILL} alt="Superbill" className="w-full h-full object-cover" />
              )}
            </div>
            <div style={{ padding: '8px 16px', borderTop: '0.5px solid #f0f0f0' }}>
              <p style={{ fontSize: 11, color: '#6b5ce7', cursor: 'pointer' }}>Can&rsquo;t read something? Scan with Lens again &rarr;</p>
            </div>
          </div>

          {/* Right: Extracted Data */}
          <div style={{ background: '#fff', border: '0.5px solid #e8e8e8', borderRadius: 12, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            <div className="flex items-center justify-between" style={{ padding: '10px 16px', borderBottom: '0.5px solid #f0f0f0' }}>
              <span style={{ fontSize: 13, fontWeight: 500, color: '#111' }}>AI-extracted data</span>
              {data && (
                reviewCount === 0
                  ? <span style={{ fontSize: 11, background: '#f0fdf4', color: '#16a34a', borderRadius: 20, padding: '2px 8px', fontWeight: 500 }}>All verified</span>
                  : <span style={{ fontSize: 11, background: '#fffbeb', color: '#92400e', borderRadius: 20, padding: '2px 8px', fontWeight: 500 }}>{reviewCount} fields need review</span>
              )}
            </div>

            {loading ? (
              <div className="flex-1 p-4 space-y-4">
                {[1, 2, 3, 4].map(i => <div key={i} className="h-14 bg-slate-100 rounded animate-pulse" />)}
              </div>
            ) : !data ? (
              <div
                className="flex-1 p-6 flex items-start gap-3 bg-amber-50/40"
                data-testid="extract-error-banner"
              >
                <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
                <div>
                  <p className="text-[13px] font-medium text-amber-900">AI extraction unavailable</p>
                  <p className="text-[11px] text-amber-800 mt-1">{extractError || 'No extracted data available.'}</p>
                  <button
                    onClick={() => navigate('/revq/claims/new')}
                    className="mt-3 text-[11px] font-medium text-violet-700 hover:underline"
                    style={{ background: 'none', border: 'none', cursor: 'pointer' }}
                    data-testid="ai-claims-manual-link"
                  >
                    Open the manual claim wizard &rarr;
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex-1 overflow-y-auto" style={{ padding: '12px 16px' }}>
                {/* Patient Section */}
                <p style={{ fontSize: 10, color: '#aaa', textTransform: 'uppercase', marginBottom: 8, letterSpacing: '0.05em' }}>Patient</p>
                <ConfField label="Patient name" value={data.patient.name.v} confidence={data.patient.name.c} />
                <ConfField label="DOB" value={data.patient.dob.v} confidence={data.patient.dob.c} />
                <ConfField label="Member ID" value={data.patient.memberId.v} confidence={data.patient.memberId.c} />
                <ConfField label="Insurance" value={data.patient.insurance.v} confidence={data.patient.insurance.c} />

                <div style={{ borderTop: '0.5px solid #f0f0f0', margin: '12px 0' }} />

                {/* Provider Section */}
                <p style={{ fontSize: 10, color: '#aaa', textTransform: 'uppercase', marginBottom: 8, letterSpacing: '0.05em' }}>Provider</p>
                <ConfField label="Provider name" value={data.provider.name.v} confidence={data.provider.name.c} />
                <ConfField label="NPI" value={data.provider.npi.v} confidence={data.provider.npi.c} />
                <ConfField label="DOS" value={data.provider.dos.v} confidence={data.provider.dos.c} />
                <ConfField label="Location" value={data.provider.location.v} confidence={data.provider.location.c} />

                <div style={{ borderTop: '0.5px solid #f0f0f0', margin: '12px 0' }} />

                {/* Diagnoses */}
                <p style={{ fontSize: 10, color: '#aaa', textTransform: 'uppercase', marginBottom: 8, letterSpacing: '0.05em' }}>Diagnoses</p>
                {data.diagnoses.map((dx, i) => (
                  <div key={i} className="flex items-center gap-2" style={{ marginBottom: 6, padding: '6px 0' }}>
                    <span style={{ fontSize: 11, fontFamily: 'monospace', color: '#555', width: 56, flexShrink: 0 }}>{dx.code}</span>
                    <span style={{ fontSize: 12, color: '#333', flex: 1 }}>{dx.desc}</span>
                    <div className="flex items-center gap-1">
                      <div style={{ width: 6, height: 6, borderRadius: '50%', background: dx.c >= 95 ? '#22c55e' : '#f59e0b' }} />
                      <span style={{ fontSize: 10, color: dx.c >= 95 ? '#22c55e' : '#f59e0b' }}>{dx.c}%</span>
                    </div>
                  </div>
                ))}
                <button style={{ fontSize: 11, color: '#6b5ce7', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 500, marginTop: 4 }}>
                  <Plus className="h-3 w-3 inline" style={{ verticalAlign: '-2px' }} /> Add diagnosis
                </button>

                <div style={{ borderTop: '0.5px solid #f0f0f0', margin: '12px 0' }} />

                {/* Procedures */}
                <p style={{ fontSize: 10, color: '#aaa', textTransform: 'uppercase', marginBottom: 8, letterSpacing: '0.05em' }}>Procedures</p>
                {data.procedures.map((p, i) => (
                  <div key={i} className="flex items-center gap-2" style={{ marginBottom: 6, padding: '6px 0' }}>
                    <span style={{ fontSize: 11, fontFamily: 'monospace', color: '#555', width: 56, flexShrink: 0 }}>{p.cpt}</span>
                    <span style={{ fontSize: 12, color: '#333', flex: 1 }}>{p.desc}</span>
                    <span style={{ fontSize: 12, fontWeight: 500, color: '#111' }}>{formatCurrency(p.fee)}</span>
                  </div>
                ))}
                <button style={{ fontSize: 11, color: '#6b5ce7', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 500, marginTop: 4 }}>
                  <Plus className="h-3 w-3 inline" style={{ verticalAlign: '-2px' }} /> Add procedure
                </button>
              </div>
            )}

            {/* Footer */}
            {data && (
              <div style={{ padding: '10px 16px', borderTop: '0.5px solid #f0f0f0' }}>
                <p style={{ fontSize: 11, color: '#888', marginBottom: 8 }}>
                  Lens extracted {Object.keys(data.patient).length + Object.keys(data.provider).length + data.diagnoses.length + data.procedures.length} fields &middot; {Object.values({ ...data.patient, ...data.provider }).filter(f => f.c >= 95).length} verified &middot; {reviewCount} need review
                </p>
                <button
                  onClick={() => setStep(2)}
                  disabled={lowCount > 0}
                  style={{
                    width: '100%', height: 40, borderRadius: 8,
                    background: lowCount > 0 ? '#e0e0e0' : '#111',
                    color: lowCount > 0 ? '#aaa' : '#fff',
                    fontSize: 13, fontWeight: 500, border: 'none', cursor: lowCount > 0 ? 'not-allowed' : 'pointer',
                  }}
                  title={lowCount > 0 ? `Resolve ${lowCount} low-confidence fields above to continue` : ''}
                  data-testid="continue-to-validation"
                >
                  Continue to validation &rarr;
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ═══ STEP 2 — Validate ═══ */}
      {step === 2 && (
        <div style={{ maxWidth: 720, margin: '0 auto' }} className="space-y-4">
          {!validation ? (
            <div className="py-16 text-center">
              <Loader2 className="h-8 w-8 animate-spin text-slate-400 mx-auto mb-3" />
              <p style={{ fontSize: 13, color: '#888' }}>Running AI validation checks...</p>
            </div>
          ) : (
            <>
              {/* Score Card */}
              <div style={{ background: '#fff', border: '0.5px solid #e8e8e8', borderRadius: 12, padding: '16px 20px' }} data-testid="score-card">
                <div className="flex items-center justify-between">
                  <div>
                    <p style={{ fontSize: 11, color: '#aaa' }}>Claim score</p>
                    <p style={{ fontSize: 32, fontWeight: 500, color: '#111' }}>{validation.score}%</p>
                  </div>
                  <span style={{
                    fontSize: 13, fontWeight: 500, height: 32, padding: '0 14px', borderRadius: 8,
                    background: '#dcfce7', color: '#166534', border: '0.5px solid #bbf7d0',
                    display: 'flex', alignItems: 'center',
                  }}>{validation.status}</span>
                </div>
                {/* Progress bar */}
                <div style={{ height: 6, borderRadius: 3, background: '#f0f0f0', marginTop: 12 }}>
                  <div style={{ height: 6, borderRadius: 3, background: scoreColor, width: `${validation.score}%`, transition: 'width 0.6s ease' }} />
                </div>
                {/* Meta row */}
                <div className="flex items-center justify-between" style={{ marginTop: 10 }}>
                  <div className="flex items-center gap-5">
                    <span className="flex items-center gap-1" style={{ fontSize: 12, color: '#555' }}>
                      <DollarSign className="h-3.5 w-3.5" style={{ color: '#16a34a' }} />
                      Est. reimbursement: <strong style={{ color: '#16a34a' }}>{formatCurrency(validation.estReimburse)}</strong>
                    </span>
                    <span className="flex items-center gap-1" style={{ fontSize: 12, color: '#555' }}>
                      <Clock className="h-3.5 w-3.5" style={{ color: '#888' }} />
                      Est. payment: <strong>{validation.estDays} days</strong>
                    </span>
                  </div>
                  {warningCount > 0 && (
                    <span style={{ fontSize: 12, color: '#d97706', fontWeight: 500 }}>
                      <AlertTriangle className="h-3 w-3 inline" style={{ verticalAlign: '-2px' }} /> {warningCount} item needs your attention
                    </span>
                  )}
                </div>
              </div>

              {/* Compliance Checks */}
              <div>
                <p style={{ fontSize: 12, fontWeight: 500, color: '#111', marginBottom: 10 }}>Compliance checks</p>
                <div className="space-y-2">
                  {validation.checks.map((check, i) => (
                    <div key={i} style={{
                      background: check.pass ? '#f0fdf4' : acknowledged.has(check.name) ? '#f9fafb' : '#fffbeb',
                      border: `0.5px solid ${check.pass ? '#bbf7d0' : acknowledged.has(check.name) ? '#e8e8e8' : '#fde68a'}`,
                      borderRadius: 8, padding: check.pass ? '10px 14px' : '12px 14px',
                    }} className="flex items-start gap-3" data-testid={`check-${i}`}>
                      {check.pass ? (
                        <div style={{ width: 18, height: 18, borderRadius: '50%', background: '#22c55e', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1 }}>
                          <Check className="h-2.5 w-2.5 text-white" />
                        </div>
                      ) : (
                        <div style={{ width: 18, height: 18, borderRadius: '50%', background: '#fef3c7', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1, color: '#92400e', fontSize: 11, fontWeight: 700 }}>!</div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p style={{ fontSize: 12, fontWeight: 500, color: '#111' }}>{check.name}</p>
                        <p style={{ fontSize: check.pass ? 11 : 12, color: '#666' }}>{check.msg}</p>
                      </div>
                      {!check.pass && !acknowledged.has(check.name) && (
                        <button
                          onClick={() => setAcknowledged(prev => new Set([...prev, check.name]))}
                          style={{ fontSize: 11, color: '#6b5ce7', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 500, whiteSpace: 'nowrap' }}
                          data-testid={`ack-${i}`}
                        >Acknowledge &rarr;</button>
                      )}
                      {!check.pass && acknowledged.has(check.name) && (
                        <CheckCircle2 className="h-4 w-4 text-gray-400 shrink-0" />
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* AI Recommendations */}
              <div style={{ background: '#f5f3ff', border: '0.5px solid #ddd6fe', borderRadius: 10, padding: '14px 16px' }} data-testid="ai-recommendations">
                <div className="flex items-center gap-2" style={{ marginBottom: 10 }}>
                  <Sparkles className="h-3.5 w-3.5" style={{ color: '#6b5ce7' }} />
                  <span style={{ fontSize: 12, fontWeight: 500, color: '#5b21b6' }}>AI recommendations</span>
                  <span style={{ fontSize: 10, color: '#aaa' }}>(optional)</span>
                </div>
                {validation.recs.map((rec, i) => (
                  <div key={i} className="flex items-start gap-2" style={{
                    padding: '8px 0',
                    borderTop: i > 0 ? '0.5px solid rgba(107,92,231,0.1)' : 'none',
                  }}>
                    <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#6b5ce7', marginTop: 6, flexShrink: 0 }} />
                    <p style={{ fontSize: 12, color: '#3730a3', lineHeight: 1.5 }}>{rec}</p>
                  </div>
                ))}
                <p style={{ fontSize: 11, color: '#7c6fcd', fontStyle: 'italic', marginTop: 8 }}>These are optional — they may increase reimbursement but the claim is valid without them.</p>
              </div>

              {/* Footer buttons */}
              <div className="flex items-center justify-between" style={{ paddingTop: 8 }}>
                <button onClick={() => setStep(1)} style={{ fontSize: 13, color: '#555', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
                  <ArrowLeft className="h-3.5 w-3.5" /> Back
                </button>
                <button
                  onClick={() => setStep(3)}
                  style={{ height: 40, padding: '0 20px', borderRadius: 8, background: '#111', color: '#fff', fontWeight: 500, fontSize: 13, border: 'none', cursor: 'pointer' }}
                  data-testid="next-to-submit"
                >Next — review & submit &rarr;</button>
              </div>
            </>
          )}
        </div>
      )}

      {/* ═══ STEP 3 — Submit ═══ */}
      {step === 3 && data && (
        <div style={{ maxWidth: 680, margin: '0 auto' }} className="space-y-4">
          {/* Claim Summary Card */}
          <div style={{ background: '#fff', border: '0.5px solid #e8e8e8', borderRadius: 12, padding: 20, overflow: 'hidden' }} data-testid="claim-summary">
            {/* Patient + Provider */}
            <div className="grid grid-cols-2 gap-6" style={{ paddingBottom: 16, borderBottom: '0.5px solid #f0f0f0', marginBottom: 16 }}>
              <div>
                <p style={{ fontSize: 10, color: '#aaa', textTransform: 'uppercase', marginBottom: 6 }}>Patient</p>
                <p style={{ fontSize: 15, fontWeight: 500, color: '#111' }}>{data.patient.name.v}</p>
                <p style={{ fontSize: 12, color: '#666', lineHeight: 1.7 }}>{data.patient.insurance.v}</p>
                <p style={{ fontSize: 12, color: '#666' }}>Member: {data.patient.memberId.v}</p>
              </div>
              <div>
                <p style={{ fontSize: 10, color: '#aaa', textTransform: 'uppercase', marginBottom: 6 }}>Provider</p>
                <p style={{ fontSize: 15, fontWeight: 500, color: '#111' }}>{data.provider.name.v}</p>
                <p style={{ fontSize: 12, color: '#666', lineHeight: 1.7 }}>NPI: {data.provider.npi.v}</p>
                <p style={{ fontSize: 12, color: '#666' }}>DOS: {data.provider.dos.v}</p>
              </div>
            </div>

            {/* Charges */}
            <p style={{ fontSize: 10, color: '#aaa', textTransform: 'uppercase', marginBottom: 8 }}>Charges</p>
            {data.procedures.map((p, i) => (
              <div key={i} className="flex items-center" style={{ borderBottom: '0.5px solid #f5f5f5', padding: '7px 0' }}>
                <span style={{ fontSize: 11, fontFamily: 'monospace', color: '#555', width: 56, flexShrink: 0 }}>{p.cpt}</span>
                <span style={{ fontSize: 12, color: '#333', flex: 1 }}>{p.desc}</span>
                <span style={{ fontSize: 12, fontWeight: 500, color: '#111' }}>{formatCurrency(p.fee)}</span>
              </div>
            ))}
            <div style={{ paddingTop: 12, borderTop: '0.5px solid #e8e8e8', marginTop: 8 }}>
              <div className="flex justify-between" style={{ marginBottom: 4 }}>
                <span style={{ fontSize: 13 }}>Total charges</span>
                <span style={{ fontSize: 13, fontWeight: 500 }}>{formatCurrency(data.total)}</span>
              </div>
              <div className="flex justify-between">
                <span style={{ fontSize: 13, fontWeight: 500 }}>Estimated reimbursement</span>
                <span style={{ fontSize: 13, fontWeight: 500, color: '#16a34a' }}>{formatCurrency(validation?.estReimburse || 0)}</span>
              </div>
            </div>

            {/* Submission method */}
            <div style={{ marginTop: 16, paddingTop: 16, borderTop: '0.5px solid #f0f0f0' }}>
              <p style={{ fontSize: 10, color: '#aaa', textTransform: 'uppercase', marginBottom: 6 }}>Submission method</p>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-3.5 w-3.5" style={{ color: '#22c55e' }} />
                <span style={{ fontSize: 13, color: '#333' }}>Electronic (EDI 837) — recommended</span>
                <button style={{ fontSize: 11, color: '#6b5ce7', background: 'none', border: 'none', cursor: 'pointer', marginLeft: 4 }}>Change</button>
              </div>
            </div>

            {/* HIPAA */}
            <div className="flex items-center gap-2" style={{ background: '#f9fafb', borderRadius: 6, padding: '8px 12px', marginTop: 12 }}>
              <Lock className="h-3.5 w-3.5" style={{ color: '#aaa' }} />
              <span style={{ fontSize: 11, color: '#888' }}>Encrypted and transmitted securely &middot; Full audit trail maintained</span>
            </div>
          </div>

          {/* Score reminder */}
          <p style={{ fontSize: 12, color: '#555', textAlign: 'center' }}>
            Claim score: {validation?.score}% &middot; {validation?.status} &middot; Est. reimbursement {formatCurrency(validation?.estReimburse || 0)}
          </p>

          {/* Submit button */}
          <button
            onClick={handleSubmit}
            disabled={submitting}
            style={{
              width: '100%', height: 48, borderRadius: 8,
              background: submitting ? '#86efac' : '#16a34a',
              color: '#fff', fontSize: 14, fontWeight: 500, border: 'none', cursor: submitting ? 'wait' : 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            }}
            className="hover:bg-[#15803d] transition-colors"
            data-testid="submit-claim-btn"
          >
            {submitting ? (
              <><Loader2 className="h-4 w-4 animate-spin" /> Submitting...</>
            ) : (
              <>
                Submit claim &rarr;
                <span style={{ background: 'rgba(255,255,255,0.2)', borderRadius: 10, padding: '2px 8px', fontSize: 11, marginLeft: 4 }}>3 credits</span>
              </>
            )}
          </button>

          {/* Back */}
          <div className="text-center">
            <button onClick={() => setStep(2)} style={{ fontSize: 12, color: '#555', background: 'none', border: 'none', cursor: 'pointer' }}>
              <ArrowLeft className="h-3 w-3 inline" style={{ verticalAlign: '-2px', marginRight: 4 }} /> Back to validation
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default RevQAIClaimsWizard;
